import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';
import {initHorizontalScroll} from './horizontal-scroll.js';

// ─────────────────────────────────────────────────────────────────────────────
// LAB ADVENTURES  →  pagina /lab/digital-adventures
//
// Desktop (≥992px): scroll orizzontale
//   hero → pannello parole (4 sezioni) → final-1 → final-2
//   Il pin + timeline + snap sono delegati a `horizontal-scroll`.
//
// Mobile (<992px): scroll orizzontale semplificato — implementazione locale
//   hero → accordion → final-1 → final-2
//   (il pannello --word è d-none su mobile → 0 larghezza, ignorato)
// ─────────────────────────────────────────────────────────────────────────────

function initLabAdventures() {
    const wrapper = document.querySelector('.lab-full');
    if (!wrapper) return null;

    const isDesktop = window.matchMedia('(min-width: 992px)').matches;

    if (isDesktop) {
        return _initDesktop(wrapper);
    } else {
        return _initMobile(wrapper);
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// DESKTOP — scroll orizzontale con 4 sezioni parola
// ─────────────────────────────────────────────────────────────────────────────
function _initDesktop(wrapper) {
    const track     = wrapper.querySelector('.lab-full__track');
    const wordPanel = wrapper.querySelector('.lab-full__panel--word');
    const final1    = wrapper.querySelector('.lab-full__panel--final-1');
    const final2    = wrapper.querySelector('.lab-full__panel--final-2');
    if (!track || !wordPanel) return null;

    // Pannelli desktop in flow.
    // Escludiamo accordion e i 4 word-mobile (entrambi d-lg-none → offsetLeft=0,
    // altrimenti il timeline orizzontale tenta di tornare a x=0 e annulla la dwell).
    const allPanels = Array.from(track.querySelectorAll(':scope > .lab-full__panel'));
    const panels = allPanels.filter(p =>
        !p.classList.contains('lab-full__panel--accordion') &&
        !p.classList.contains('lab-full__panel--word-mobile')
    );
    const wordIdx = panels.indexOf(wordPanel);

    const words    = gsap.utils.toArray('.lab-adv__word', wordPanel);
    const sections = gsap.utils.toArray('.lab-adv__section', wordPanel);
    const numWords = words.length;


    const TRANS_SCROLL = 1400;
    const WORD_SCROLL  = 1800;
    const HOLD_SCROLL  = 1000;

    function activateSection(idx) {
        sections.forEach((s, i) => s.classList.toggle('lab-adv__section--active', i === idx));
        words.forEach((w, i) => w.classList.toggle('lab-adv__word--active', i === idx));
    }

    let lastWordIdx = -1;

    const handle = initHorizontalScroll({
        outer: wrapper,                      // .lab-full ( = .js-h-outer)
        panels,                              // filtrato (no accordion su desktop)
        startBtn: null,                      // bottoni custom gestiti sotto
        useGenericMobile: false,
        transitionScroll: TRANS_SCROLL,
        holdScroll: HOLD_SCROLL,
        dwells: numWords ? {[wordIdx]: numWords * WORD_SCROLL} : {},
        getPanelX: (panel) => -(panel.offsetLeft),
        scrub: 1.5,
        snapDuration: {min: 0.3, max: 0.8},
        snapEase: 'power3.inOut',
        onUpdate(self, ctx) {
            const {phase, localProg} = ctx;
            if (phase.type !== 'dwell' || phase.panelIndex !== wordIdx) return;
            const activeWord = Math.min(Math.floor(localProg * numWords), numWords - 1);
            if (activeWord !== lastWordIdx) {
                activateSection(activeWord);
                lastWordIdx = activeWord;
            }
        },
    });

    if (!handle) return null;

    const easeInOut = t => t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2;

    function scrollTo(pos, dur = 1.0) {
        if (window.lenis) {
            window.lenis.scrollTo(pos, {duration: dur, easing: easeInOut});
        } else {
            window.scrollTo({top: pos, behavior: 'smooth'});
        }
    }

    function pinScroll(frac) {
        const st = handle.st;
        return st.start + (st.end - st.start) * frac;
    }

    function dwellPhase() {
        return handle.phases.find(p => p.type === 'dwell' && p.panelIndex === wordIdx);
    }

    wrapper.querySelectorAll('.lab-full__next-btn').forEach(btn => {
        btn.addEventListener('click', e => {
            e.preventDefault();
            if (!handle.st || !handle.phases.length) return;
            scrollTo(handle.st.start + handle.phases[0].len, 1.0);
        });
    });

    // Freccia → salta direttamente al primo pannello nero (final-1)
    wrapper.querySelectorAll('.lab-full__goto-final').forEach(btn => {
        btn.addEventListener('click', () => {
            if (!handle.st) return;
            const final1Idx = panels.indexOf(final1);
            const arrive = handle.phases.find(p => p.type === 'transition' && p.toIndex === final1Idx);
            if (!arrive) return;
            scrollTo(pinScroll(arrive.f1), 1.2);
        });
    });

    // Freccia ← torna all'inizio del pannello parole (prima word)
    wrapper.querySelectorAll('.lab-full__goto-start').forEach(btn => {
        btn.addEventListener('click', () => {
            if (!handle.st) return;
            const dwell = dwellPhase();
            if (!dwell) return;
            scrollTo(pinScroll(dwell.f0), 1.2);
        });
    });

    words.forEach((word, i) => {
        word.addEventListener('click', () => {
            if (!handle.st) return;
            const dwell = dwellPhase();
            if (!dwell) return;
            const frac = dwell.f0 + (i / numWords) * (dwell.f1 - dwell.f0);
            scrollTo(pinScroll(frac), 1.0);
        });
    });

    window.addEventListener('resize', () => {
        lastWordIdx = -1;
    });

    return handle.st;
}

// ─────────────────────────────────────────────────────────────────────────────
// MOBILE — swipe nav (come team page): nessun pin, track tradotta via transform.
// Dentro al pannello accordion (overflow-y: auto) la nav lascia passare lo swipe
// quando il contenuto è scrollabile in quella direzione → niente scroll "scattoso".
// ─────────────────────────────────────────────────────────────────────────────
function _initMobile(wrapper) {
    const track = wrapper.querySelector('.lab-full__track');
    if (!track) return null;

    const allPanels = Array.from(track.querySelectorAll(':scope > .lab-full__panel'));
    // Su mobile escludiamo solo il pannello desktop (.--word senza .--word-mobile);
    // i 4 pannelli mobile parola (.--word-mobile) restano in flow.
    const panels = allPanels.filter(p => {
        return !(p.classList.contains('lab-full__panel--word') && !p.classList.contains('lab-full__panel--word-mobile'));
    });
    if (!panels.length) return null;

    const scrollSvg = document.querySelector('.hs__scroll-global');
    const firstWordPanel = wrapper.querySelector('.lab-full__panel--word-mobile');
    const firstWordIdx = firstWordPanel ? panels.indexOf(firstWordPanel) : -1;

    let sectionIndex = 0;

    function goToSection(i, animate) {
        sectionIndex = Math.max(0, Math.min(i, panels.length - 1));
        track.style.transition = animate === false ? 'none' : '';
        track.style.transform = 'translate3d(' + (-sectionIndex * window.innerWidth) + 'px,0,0)';
        const isLast = sectionIndex >= panels.length - 1;
        wrapper.classList.toggle('is-last-panel', isLast);
        if (scrollSvg) {
            scrollSvg.classList.toggle('--hidden', isLast || isAtFooter);
        }
    }
    goToSection(0, false);

    function panelCanScroll(fwd) {
        const current = panels[sectionIndex];
        if (!current) return false;
        const max = current.scrollHeight - current.clientHeight;
        if (max <= 1) return false;
        return fwd ? current.scrollTop < max - 1 : current.scrollTop > 0;
    }

    let startY = 0, startX = 0, gesture = null;
    let dragActive = false, dragBase = 0;
    let lastDy = 0, lastT = 0, velocity = 0;
    let isAtFooter = false;

    function onScroll() {
        isAtFooter = window.scrollY > wrapper.offsetHeight * 0.3;
        if (scrollSvg) {
            const isLast = sectionIndex >= panels.length - 1;
            scrollSvg.classList.toggle('--hidden', isLast || isAtFooter);
        }
    }
    window.addEventListener('scroll', onScroll, {passive: true});
    onScroll();

    function onStart(e) {
        startY = e.touches[0].clientY;
        startX = e.touches[0].clientX;
        gesture = null;
        dragActive = false;
        lastDy = 0;
        velocity = 0;
        lastT = e.timeStamp || performance.now();
    }

    function onMove(e) {
        const dy = e.touches[0].clientY - startY;
        const dx = e.touches[0].clientX - startX;

        if (gesture === null && (Math.abs(dy) > 6 || Math.abs(dx) > 6)) {
            if (Math.abs(dy) < Math.abs(dx)) {
                gesture = 'pass';
                return;
            }
            const fwd = dy < 0;
            // Dentro l'accordion: se il contenuto interno può scrollare in quella
            // direzione, lascia il controllo allo scroll nativo del pannello.
            if (panelCanScroll(fwd)) {
                gesture = 'pass';
                return;
            }
            const canAct = fwd
                ? (!isAtFooter && sectionIndex < panels.length - 1)
                : (!isAtFooter && sectionIndex > 0);
            gesture = canAct ? 'hijack' : 'pass';
            if (gesture === 'hijack') {
                dragActive = true;
                dragBase = -sectionIndex * window.innerWidth;
                track.style.transition = 'none';
            }
        }

        if (gesture === 'hijack' && dragActive) {
            e.preventDefault();
            e.stopPropagation();
            const now = e.timeStamp || performance.now();
            const dt = now - lastT;
            if (dt > 0) velocity = (dy - lastDy) / dt;
            lastDy = dy;
            lastT = now;
            let offset = dy;
            if ((dragBase + offset) > 0) offset = offset * 0.3;
            track.style.transform = 'translate3d(' + (dragBase + offset) + 'px,0,0)';
        }
    }

    function onEnd(e) {
        if (gesture !== 'hijack' || !dragActive) {
            gesture = null;
            dragActive = false;
            return;
        }
        const dy = e.changedTouches[0].clientY - startY;
        const distThresh = window.innerWidth * 0.18;
        const velThresh = 0.4;
        const goNext = dy < -distThresh || velocity < -velThresh;
        const goPrev = dy > distThresh || velocity > velThresh;

        track.style.transition = '';

        if (goNext && sectionIndex < panels.length - 1) {
            goToSection(sectionIndex + 1, true);
        } else if (goPrev && sectionIndex > 0) {
            goToSection(sectionIndex - 1, true);
        } else {
            goToSection(sectionIndex, true);
        }

        gesture = null;
        dragActive = false;
    }

    window.addEventListener('touchstart', onStart, {passive: true});
    window.addEventListener('touchmove', onMove, {passive: false});
    window.addEventListener('touchend', onEnd, {passive: true});

    // CTA hero → vai al primo pannello parola mobile
    wrapper.querySelectorAll('.lab-full__next-btn').forEach(btn => {
        btn.addEventListener('click', e => {
            e.preventDefault();
            if (firstWordIdx >= 0) goToSection(firstWordIdx, true);
            else goToSection(1, true);
        });
    });

    window.addEventListener('resize', () => {
        goToSection(sectionIndex, false);
    });

    return null;
}

export {initLabAdventures};
